import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { WebhookEventService } from '../../Modules/Ops/Services/webhook-event.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { ROLE_WEBHOOK_REPLAY, WEBHOOK_REPLAYED_MESSAGE } from '../../Consts/ops.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { WebhookEvent } from '../../Models/backoffice/webhook-event.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  WebhookEventFilters,
  WebhookEventListResponse,
  WebhookEventShowResponse,
} from '../../Interfaces/api/backoffice/webhook-event.interface';

@Injectable()
export class WebhookEventsFacade {
  private readonly _webhookService = inject(WebhookEventService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _selectedEventId = signal<string>('');
  private readonly _isMutating = signal<boolean>(false);

  private readonly _filters = signal<WebhookEventFilters>({
    event_type: '',
    processing_status: '',
    stripe_event_id: '',
    from: '',
    to: '',
  });

  private readonly _events = httpResource<LaravelDefaultResponse<WebhookEventListResponse>>(() =>
    this._webhookService.listRequest(this._filters(), this._page(), this._pageSize()),
  );

  private readonly _event = httpResource<LaravelDefaultResponse<WebhookEventShowResponse>>(() =>
    this._selectedEventId()
      ? this._webhookService.showRequest(this._selectedEventId())
      : undefined,
  );

  readonly filters = computed<WebhookEventFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly events = computed<WebhookEvent[]>(() =>
    this._webhookService.toEvents(this._resourceState.valueOf(this._events)),
  );

  readonly total = computed<number>(() => this._webhookService.toTotal(this._resourceState.valueOf(this._events)));

  readonly types = computed<string[]>(() => this._webhookService.toTypes(this._resourceState.valueOf(this._events)));

  readonly statuses = computed<string[]>(() =>
    this._webhookService.toStatuses(this._resourceState.valueOf(this._events)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._events.status()),
  );

  readonly selectedEventId = computed<string>(() => this._selectedEventId());

  readonly selectedEvent = computed<WebhookEvent | null>(() =>
    this._webhookService.toEvent(this._resourceState.valueOf(this._event)),
  );

  readonly selectedEventState = computed<MetricState>(() =>
    this._resourceState.metricState(this._event.status()),
  );

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly canReplay = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_WEBHOOK_REPLAY) && !this._isMutating(),
  );

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  applyFilters(filters: WebhookEventFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({
      event_type: '',
      processing_status: '',
      stripe_event_id: '',
      from: '',
      to: '',
    });
  }

  selectEvent(eventId: string): void {
    this._selectedEventId.set(eventId);
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._events.reload();
    this._event.reload();
  }

  replay(eventId: string): void {
    if (!this.canReplay()) {
      return;
    }

    this._isMutating.set(true);

    this._webhookService
      .replay(eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(): void {
    this._isMutating.set(false);
    this.reload();
    this._msgService.showSnackBar(WEBHOOK_REPLAYED_MESSAGE, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
